import React, { useState, useRef } from "react";
import { Button } from "../../ui/Button/Button";
import { MessageStrip } from "../../ui/MessageStrip/MessageStrip";
import { cn } from "../../../lib/cn";
import ImageCropModal from "../ImageCropModal";
import { readFile } from "../../../utils/cropImage";
import "@ui5/webcomponents-icons/dist/camera.js";
import "@ui5/webcomponents-icons/dist/delete.js";

/**
 * ImageUpload Component
 * Allows users to select and preview images before upload
 *
 * @param {Object} props
 * @param {File|null} props.value - Current image file
 * @param {Function} props.onChange - Callback when image changes (file)
 * @param {string} props.currentImageUrl - URL of existing image
 * @param {string} props.label - Label text
 * @param {string} props.error - Error message
 * @param {number} props.maxSizeMB - Max file size in MB (default: 5)
 * @param {string[]} props.acceptedFormats - Accepted file formats (default: jpg, jpeg, png, gif, webp)
 */
export function ImageUpload({
  value,
  onChange,
  currentImageUrl,
  label = "Upload Image",
  error,
  maxSizeMB = 5,
  acceptedFormats = ["jpg", "jpeg", "png", "gif", "webp"],
}) {
  const [preview, setPreview] = useState(currentImageUrl || null);
  const [localError, setLocalError] = useState("");
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [originalFileName, setOriginalFileName] = useState("");
  const fileInputRef = useRef(null);

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const fileExtension = file.name.split(".").pop()?.toLowerCase();
    if (!acceptedFormats.includes(fileExtension)) {
      setLocalError(
        `Invalid file type. Accepted: ${acceptedFormats.join(", ")}`
      );
      return;
    }

    // Validate file size
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > maxSizeMB) {
      setLocalError(`File size exceeds ${maxSizeMB}MB limit`);
      return;
    }

    // Clear errors and open crop modal
    setLocalError("");
    setOriginalFileName(file.name);

    try {
      const imageDataUrl = await readFile(file);
      setImageToCrop(imageDataUrl);
      setCropModalOpen(true);
    } catch (error) {
      console.error("Error reading file:", error);
      setLocalError("Failed to load image. Please try again.");
    }
  };

  const handleRemove = () => {
    setPreview(null);
    setLocalError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onChange?.(null);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleCropComplete = (croppedImageBlob) => {
    // Convert blob to File object with original filename
    const croppedFile = new File(
      [croppedImageBlob],
      originalFileName,
      { type: "image/jpeg" }
    );

    // Create preview from blob
    const previewUrl = URL.createObjectURL(croppedImageBlob);
    setPreview(previewUrl);

    // Pass cropped file to parent
    onChange?.(croppedFile);
  };

  const handleCropModalClose = () => {
    setCropModalOpen(false);
    setImageToCrop(null);
    // Reset file input if user cancels
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const displayError = error || localError;

  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}

      {displayError && (
        <MessageStrip design="Negative" className="text-sm">
          {displayError}
        </MessageStrip>
      )}

      <div className="flex items-start gap-4">
        {/* Preview Area */}
        <div
          className={cn(
            "relative flex-shrink-0 w-32 h-32 rounded-lg border-2 border-dashed overflow-hidden",
            preview ? "border-blue-500" : "border-gray-300 bg-gray-50"
          )}
        >
          {preview ? (
            <>
              <img
                src={preview}
                alt="Preview"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
                <Button
                  icon="delete"
                  design="Negative"
                  onClick={handleRemove}
                  className="opacity-0 hover:opacity-100 transition-opacity"
                >
                  Remove
                </Button>
              </div>
            </>
          ) : (
            <div
              className="w-full h-full flex flex-col items-center justify-center cursor-pointer hover:bg-gray-100 transition-colors"
              onClick={handleClick}
            >
              <ui5-icon name="camera" class="text-gray-400 text-3xl mb-2" />
              <span className="text-xs text-gray-500">Click to upload</span>
            </div>
          )}
        </div>

        {/* Upload Button and Info */}
        <div className="flex-1 space-y-2">
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptedFormats.map((f) => `.${f}`).join(",")}
            onChange={handleFileSelect}
            className="hidden"
          />

          {!preview && (
            <Button icon="camera" design="Default" onClick={handleClick}>
              Choose Image
            </Button>
          )}

          <div className="text-xs text-gray-500 space-y-1">
            <p>Max size: {maxSizeMB}MB</p>
            <p>Formats: {acceptedFormats.join(", ").toUpperCase()}</p>
          </div>
        </div>
      </div>

      {/* Image Crop Modal */}
      <ImageCropModal
        open={cropModalOpen}
        imageSrc={imageToCrop}
        onClose={handleCropModalClose}
        onCropComplete={handleCropComplete}
      />
    </div>
  );
}
